/**
 * Read-only embedded map for a booking's service address — customer,
 * merchant and admin booking screens all previously showed either a
 * decorative pin icon next to plain text, or (merchant only) an
 * external "Navigate" link that opens Google/Apple Maps in a new tab.
 * None of them actually rendered a map in the page itself. This is the
 * lightweight, non-interactive counterpart to ``ShopMapPicker`` (which
 * is for editing a location) — reuses the same react-leaflet + OSM
 * stack already powering ``AddressPickerModal`` / ``ShopMapPicker`` /
 * ``OrderTrackPage`` so no new mapping dependency is introduced.
 *
 * Returns null when lat/lng aren't available (legacy free-text
 * addresses have neither) so callers should keep their existing
 * text-address fallback alongside this component.
 */
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Leaflet's default marker icon path breaks under Webpack — same fix
// applied in ShopMapPicker.jsx / AddressPickerModal.jsx / OrderTrackPage.jsx.
// Idempotent (mergeOptions), safe to repeat across files.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function BookingLocationMap({ lat, lng, label, height = 140, className = "", testid = "booking-location-map" }) {
    if (lat == null || lng == null) return null;
    const pos = [Number(lat), Number(lng)];
    return (
        <div
            className={`rounded-xl overflow-hidden border border-border ${className}`}
            style={{ height }}
            data-testid={testid}
        >
            <MapContainer
                center={pos}
                zoom={15}
                scrollWheelZoom={false}
                dragging={false}
                doubleClickZoom={false}
                zoomControl={false}
                attributionControl={false}
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={pos}>
                    {label && <Tooltip permanent direction="top" offset={[0, -28]}>{label}</Tooltip>}
                </Marker>
            </MapContainer>
        </div>
    );
}
